import orderRepository, {
  OrderItemDto,
  OrderItemSummaryDto,
  CommentDto,
  UpdateOrderItemDto,
  HistoricOrderItemSummaryDto,
  OnHoldOrderItemSummaryDto,
  OrderItemEditLeaseDto,
  CustomerUploadedPlansAttachmentDto
} from 'api/orderRepository';
import userRepository from 'api/userRepository';
import { OrderItem } from 'models/OrderItemModel';
import { OrderItemSummary } from 'models/OrderItemSummaryModel';
import { Comment } from 'models/CommentModel';
import { statusChips } from 'data/OrderItemStatus';

import { compareDesc } from 'date-fns';
import { HistoricOrderItemSummary } from 'models/HistoricOrderItemSummaryModel';
import { ProductType } from 'data/ProductType';
import { AttachmentType } from 'data/AttachmentType';
import { PaginatedResponse } from '../models/Pagination';
import Payload from 'models/DataModel';
import { OnHoldOrderItemSummary } from 'models/OnHoldOrderItemSummaryModel';

interface UpdateOrderItem {
  status?: string;
  data?: string;
  completedBy?: string;
  qualityControlledBy?: string;
}

export interface UpdateOrderItemServiceResult {
  etag: string | null;
  lockOwnerName: string | null;
}

export interface CustomerUploadedPlansAttachment {
  attachmentUrls: URL[];
  canReplace: boolean;
  isOverride: boolean;
}

const getLockOwnerId = (error: any): string | null => {
  return error?.lockedBy ?? error?.data?.lockedBy ?? error?.data?.LockedBy ?? error?.LockedBy ?? null;
};

const withLockOwnerName = async <T>(operation: Promise<T>): Promise<T> => {
  try {
    return await operation;
  } catch (error: any) {
    const lockOwnerId = getLockOwnerId(error);

    if (error?.status === 409 && lockOwnerId) {
      throw {
        ...error,
        lockOwnerName: await userRepository.getUserName(lockOwnerId)
      };
    }

    throw error;
  }
};

const productNames: Readonly<{ [key: string]: ProductType }> = {
  SWHIPCON29ResidentialComponent: ProductType.Residential,
  HIPCON29ResidentialComponent: ProductType.Residential,
  SWHIPCON29CommercialComponent: ProductType.Commercial,
  HIPCON29CommercialComponent: ProductType.Commercial
};

const lookUpStatus = (id: string): string => Object.keys(statusChips).find((key) => statusChips[key].id === id) ?? '--';

const toComment = async (comment: CommentDto): Promise<Comment> => {
  return {
    userName: await userRepository.getUserName(comment.customerId),
    timestamp: new Date(comment.createdDate),
    text: comment.commentText
  };
};

const toOrderItemSummary = (oi: OrderItemSummaryDto): OrderItemSummary => {
  return {
    id: oi.id,
    orderReference: oi.orderItemReference,
    product: productNames[oi.productName],
    createdOn: new Date(oi.createdOn),
    status: lookUpStatus(oi.status.id)
  };
};

const toHistoricOrderItemSummary = async (oi: HistoricOrderItemSummaryDto): Promise<HistoricOrderItemSummary> => {
  return {
    id: oi.id,
    orderReference: oi.orderItemReference,
    createdOn: new Date(oi.createdOn),
    product: productNames[oi.productName],
    completedBy: oi.completedBy && (await userRepository.getUserName(oi.completedBy)),
    qualityControlledBy: oi.qualityControlledBy && (await userRepository.getUserName(oi.qualityControlledBy)),
    status: lookUpStatus(oi.status.id),
    statusCreatedOn: new Date(oi.statusCreatedOn)
  };
};

const toOnHoldOrderItemSummary = async (oi: OnHoldOrderItemSummaryDto): Promise<OnHoldOrderItemSummary> => {
  return {
    id: oi.id,
    orderReference: oi.orderItemReference,
    createdOn: new Date(oi.createdOn),
    product: productNames[oi.productName],
    status: 'On hold',
    onHoldDate: new Date(oi.onHoldDate),
    onHoldReason: oi.onHoldReason
  };
};

const toOrderItem = async (oi: OrderItemDto): Promise<OrderItem> => {
  const payload = JSON.parse(oi.data) as Payload;
  const productType = productNames[oi.productName];
  var orderItem = toOrderItemSummary(oi);

  const comments = await Promise.all(oi.comments.map(toComment));

  comments.sort((a, b) => compareDesc(a.timestamp, b.timestamp));

  return {
    ...orderItem,
    data: payload,
    comments: comments,
    productType: productType,
    canRegenerateReport: oi.canRegenerateReport
  };
};

const toCustomerUploadedPlansAttachment = (attachment: CustomerUploadedPlansAttachmentDto): CustomerUploadedPlansAttachment => ({
  attachmentUrls: attachment.attachmentUris.map((attachmentUri) => new URL(attachmentUri)),
  canReplace: attachment.canReplace,
  isOverride: attachment.isOverride
});

const orderService = {
  getPaginatedOrderItems: async (params = {}): Promise<PaginatedResponse<OrderItemSummary>> => {
    const response = await orderRepository.getPaginatedOrderItems<OrderItemSummaryDto>(params);
    const items = await Promise.all(response.items.map(toOrderItemSummary));
    return { ...response, items };
  },

  getPaginatedHistoricOrderItems: async (params = {}): Promise<PaginatedResponse<HistoricOrderItemSummary>> => {
    const response = await orderRepository.getPaginatedOrderItems<HistoricOrderItemSummaryDto>(params);
    const items = await Promise.all(response.items.map(toHistoricOrderItemSummary));
    return { ...response, items };
  },

  getPaginatedOnHoldOrderItems: async (params = {}): Promise<PaginatedResponse<OnHoldOrderItemSummary>> => {
    const response = await orderRepository.getPaginatedOrderItems<OnHoldOrderItemSummaryDto>(params);
    const items = await Promise.all(response.items.map(toOnHoldOrderItemSummary));
    return { ...response, items };
  },

  getOrderItem: async (id: string): Promise<{ orderItem: OrderItem; etag: string | null }> => {
    const response = await orderRepository.getOrderItem(id);
    const orderItem = await toOrderItem(response.orderItem);
    return { orderItem, etag: response.etag };
  },

  acquireOrderItemEditLease: async (orderItemId: string, existingLeaseId?: string | null): Promise<OrderItemEditLeaseDto> => {
    return await withLockOwnerName(orderRepository.acquireOrderItemEditLease(orderItemId, existingLeaseId));
  },

  renewOrderItemEditLease: async (orderItemId: string, leaseId: string): Promise<OrderItemEditLeaseDto> => {
    return await withLockOwnerName(orderRepository.renewOrderItemEditLease(orderItemId, leaseId));
  },

  releaseOrderItemEditLease: async (orderItemId: string, leaseId: string): Promise<void> => {
    await orderRepository.releaseOrderItemEditLease(orderItemId, leaseId);
  },

  regenerateReport: async (orderItemId: string): Promise<void> => {
    await orderRepository.regenerateReport(orderItemId);
  },

  getCustomerUploadedPlans: async (orderItemId: string): Promise<CustomerUploadedPlansAttachment> => {
    return toCustomerUploadedPlansAttachment(await orderRepository.getCustomerUploadedPlans(orderItemId));
  },

  replaceCustomerUploadedPlans: async (
    orderItemId: string,
    attachmentName: string,
    attachmentBytes: string,
    contentType: string
  ): Promise<CustomerUploadedPlansAttachment> => {
    return toCustomerUploadedPlansAttachment(
      await orderRepository.replaceCustomerUploadedPlans(orderItemId, attachmentName, attachmentBytes, contentType)
    );
  },

  getOrderItemAttachmentUrls: async (id: string, fileType: AttachmentType): Promise<URL[]> => {
    const attachmentsList = await orderRepository.getOrderItemAttachments(id, fileType);
    return Promise.all(attachmentsList.map((attachmentName) => orderRepository.getAttachmentUrl(attachmentName)));
  },

  postAttachments: async (
    orderItemId: string,
    attachmentName: string,
    attachmentBytes: string,
    fileType: string,
    contentType: string,
    editLeaseId?: string | null
  ): Promise<string> => {
    return await withLockOwnerName(orderRepository.postAttachments(orderItemId, attachmentName, attachmentBytes, fileType, contentType, editLeaseId));
  },

  deleteAttachment: async (
    orderItemId: string,
    filename: string,
    fileType: AttachmentType = AttachmentType.Plans,
    editLeaseId?: string | null
  ): Promise<void> => {
    await withLockOwnerName(orderRepository.deleteAttachment(orderItemId, filename, fileType, editLeaseId));
  },

  postComment: async (orderItemId: string, customerId: string, commentText: string, editLeaseId?: string | null): Promise<void> => {
    await withLockOwnerName(orderRepository.postComment(orderItemId, customerId, commentText, editLeaseId));
  },

  postStatusWithComment: async (
    orderItemId: string,
    customerId: string,
    statusId: string,
    commentText: string,
    editLeaseId?: string | null
  ): Promise<string> => {
    const commentId = await withLockOwnerName(
      orderRepository.postStatusWithComment(orderItemId, customerId, statusId, commentText, editLeaseId)
    );
    return commentId;
  },

  updateOrderItem: async (
    orderItemId: string,
    updates: UpdateOrderItem,
    etag?: string | null,
    editLeaseId?: string | null
  ): Promise<UpdateOrderItemServiceResult> => {
    var updateDto: UpdateOrderItemDto = {};
    if (updates.status) {
      updateDto.statusId = statusChips[updates.status]?.id;
    }

    if (updates.data) {
      updateDto.data = updates.data;
    }

    updateDto.completedBy = updates.completedBy;
    updateDto.qualityControlledBy = updates.qualityControlledBy;

    const result = await orderRepository.updateOrderItem(orderItemId, updateDto, etag, editLeaseId);

    return {
      etag: result.etag,
      lockOwnerName: result.lockOwnerId ? await userRepository.getUserName(result.lockOwnerId) : null
    };
  },

  exportOrderItemsToCSV: async (params = {}) => {
    return await orderRepository.exportOrderItemsToCSV(params);
  }
};

export default orderService;
